import React, { memo, useMemo } from "react";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";
import { isEqual } from "../../services/utils";
import useAllCrditSkusStore from "../../zustandStores/allCreditSkus";
import { Routes } from "../../routes/RouteConstants";
import { getFormattedPrice } from "../../utils/creditUtils";
import { JhText } from "designPackages/textPackage";
import LogService from "../../services/LogService";
import CREDIT_EVENTS from "../../utils/CreditEvents";
import useModalStore from "../../zustandStores/useModalStore";
import { isSaleActive } from "../../services/utils";
import { useIsMobile } from "../../hooks/useDeviceType";
import PricingCard from "./PricingCard";
import SalesContactCard from "./SalesContactCard";

interface PricingCardsProps {
  showSalesContactCard?: boolean;
  onContactSalesClick?: () => void;
}

const PricingCards: React.FC<PricingCardsProps> = memo(
  ({ showSalesContactCard = false, onContactSalesClick }) => {
    const router = useRouter();
    const storedProfile = useSelector(
      (state: any) => state.userProfileReducer?.payload?.data
    );
    const { data } = useAllCrditSkusStore(
      state => ({
        data: state.data
      }),
      (previousState, currentState) => isEqual(previousState, currentState)
    );
    const { setModalData } = useModalStore(state => ({
      setModalData: state.setModalData
    }));
    const isSaleLive = isSaleActive();
    const isMobile = useIsMobile();

    const handleBuyCta = (item: any) => {
      if (storedProfile?.denial_reason === "BLACKLIST") {
        LogService.log(CREDIT_EVENTS.BLACKLIST_REC_PURCHASE_POP_UP_SHOWN);
        setModalData({
          view: "CREDITS",
          state: "open",
          type: "blacklisted",
          onCloseButtonClick: () =>
            LogService.log(CREDIT_EVENTS.BLACKLIST_REC_PURCHASE_POP_UP_CLOSED)
        });
        return;
      }
      const queryParams: any = {};
      queryParams.skuId = item.skuId;
      const { quantity = 1 } = router.query;
      LogService.log(CREDIT_EVENTS.PLAN_SELECTION_BUY_CLICKED, {
        skuId: item.skuId,
        quantity: item.skuId === 1 ? quantity : undefined
      });
      if (item.maxQuantityAllowed > 1) {
        queryParams.quantity = quantity;
      }

      router.push({
        pathname: Routes.CHECKOUT,
        query: queryParams
      });
    };

    const filteredData = useMemo(() => {
      const filtered = data.filter(item => item.skuId !== 5);

      if (isMobile) {
        // Move recommended items to the top on mobile
        return [...filtered].sort((a, b) => {
          if (a.recommended && !b.recommended) return -1;
          if (!a.recommended && b.recommended) return 1;
          return 0;
        });
      }

      return filtered;
    }, [data, isMobile]);

    return (
      <>
        <div
          className={`tw-root grid grid-cols-1 ${
            showSalesContactCard ? "md:grid-cols-4 max-w-screen-xl" : "md:grid-cols-3 max-w-[1200px]"
          }  gap-4 px-4 md:px-10 w-full md:mt-10  mx-auto`}
        >
          {filteredData.map(item => {
            const isRecommended = item.recommended;
            const hasDiscount = item.discountPercentage > 0;
            const features = item?.features || [];
            const featureTitle = item?.featureTitle || "";

            return (
              <PricingCard
                key={item.skuId}
                item={item}
                isRecommended={isRecommended}
                hasDiscount={hasDiscount}
                isSaleLive={isSaleLive}
                features={features}
                featureTitle={featureTitle}
                onBuyClick={handleBuyCta}
              />
            );
          })}

          {/* Contact Sales Card - Show for even recruiter IDs (variant group) */}
          {showSalesContactCard && (
            <SalesContactCard onContactSalesClick={onContactSalesClick} />
          )}
        </div>
      </>
    );
  }
);

PricingCards.displayName = "PricingCards";

export default PricingCards;
